'use client';

// TrueLight OS — Google Analytics 4 Foundation
// Tracks key conversion events without exposing secrets

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// ─── GA4 initialization ───────────────────────────────────────────────────────

export function initGA(): void {
  if (typeof window === 'undefined') return;
  const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
  if (!measurementId || measurementId.includes('your-') || measurementId === '') return;
  if (window.dataLayer) return; // Already initialized

  window.dataLayer = window.dataLayer || [];
  window.gtag = function (...args: any[]) {
    window.dataLayer.push(args);
  };

  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.async = true;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false, // We track manually
  });
}

// ─── Core track function ──────────────────────────────────────────────────────

export function trackEvent(eventName: string, params: Record<string, any> = {}): void {
  if (typeof window === 'undefined') return;
  const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
  if (!measurementId || measurementId.includes('your-') || measurementId === '') return;
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', eventName, params);
}

// ─── Page view tracking ───────────────────────────────────────────────────────

export function trackPageView(path: string, title?: string): void {
  trackEvent('page_view', {
    page_path: path,
    page_title: title,
  });
}

// ─── Conversion events ────────────────────────────────────────────────────────

/** Landing page visit */
export function trackLandingPageVisit(): void {
  trackEvent('landing_page_visit', {
    event_category: 'acquisition',
  });
}

/** User clicked on a pricing plan */
export function trackPricingInteraction(planName: string, planPrice: number): void {
  trackEvent('pricing_interaction', {
    event_category: 'conversion',
    plan_name: planName,
    plan_price: planPrice,
  });
}

/** User clicked checkout / "Start Trial" on a plan */
export function trackCheckoutClick(planName: string, planPrice: number, isFounder: boolean): void {
  trackEvent('checkout_click', {
    event_category: 'conversion',
    plan_name: planName,
    plan_price: planPrice,
    is_founder_plan: isFounder,
  });
}

/** User completed signup (account created) */
export function trackSignupCompletion(method: 'email' = 'email'): void {
  trackEvent('sign_up', {
    event_category: 'acquisition',
    method,
  });
}

/** User created an organization */
export function trackOrganizationCreation(orgName: string): void {
  trackEvent('organization_created', {
    event_category: 'activation',
    org_name: orgName,
  });
}

/** User reached the dashboard for the first time */
export function trackDashboardActivation(): void {
  trackEvent('dashboard_activation', {
    event_category: 'activation',
  });
}

/** User used the AI Assistant */
export function trackAIUsed(): void {
  trackEvent('ai_assistant_used', {
    event_category: 'engagement',
  });
}

/** User completed an activation milestone */
export function trackActivationMilestone(milestoneKey: string): void {
  trackEvent('activation_milestone', {
    event_category: 'activation',
    milestone: milestoneKey,
  });
}

/** Demo section interaction */
export function trackDemoInteraction(action: string): void {
  trackEvent('demo_interaction', {
    event_category: 'engagement',
    action,
  });
}
